import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {Result} from "@vesta/error-handling-remix/result";
import {AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID} from "~/domainModels/workspaceSpecific/amaron";
import {
    amaronDisposeAction,
    type AmaronActionResponse,
} from "~/utilities/apis/amaronActionsApi";
import type {EventWithEventContainer, EventContainerWithEvents} from "~/utilities/apis/api";
import {findFirstAmaronEnquiryEventOnJourney} from "~/utilities/workspaceSpecific/amaronCallSubmit";

export type AmaronJourneyDisposeSubmitArgs = {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    journeyId: Uuid;
    dispositionId: Uuid;
    remarks: string | null;
    remarksColumnId?: Uuid;
    triggerEventAdditionalColumnValues?: Record<string, unknown>;
    journeyAdditionalColumnValues: Record<string, unknown>;
    contextEventAdditionalColumnValues?: Record<string, unknown>;
    firstEnquiryEventId?: Uuid;
    firstEnquiryStatusRaw?: unknown;
};

export function submitAmaronJourneyDispose(
    args: AmaronJourneyDisposeSubmitArgs,
): Promise<Result<AmaronActionResponse>> {
    return amaronDisposeAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        request: {
            workspaceId: args.workspaceId,
            eventContainerId: args.journeyId,
            dispositionId: args.dispositionId,
            remarks: args.remarks,
            remarksColumnId: args.remarksColumnId,
            triggerEventAdditionalColumnValues: args.triggerEventAdditionalColumnValues,
            contextEventAdditionalColumnValues: args.contextEventAdditionalColumnValues,
            eventContainerAdditionalColumnValues:
                args.journeyAdditionalColumnValues,
            firstEnquiryEventId: args.firstEnquiryEventId,
            firstEnquiryStatusRaw: args.firstEnquiryStatusRaw,
        },
    });
}

/** Side panel / list disposition: use the row event as dispose context. */
export function buildAmaronJourneyDisposeArgsFromListEvent(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    event: EventWithEventContainer;
    dispositionId: Uuid;
    remarks: string | null;
    remarksColumnId?: Uuid;
    triggerEventAdditionalColumnValues?: Record<string, unknown>;
}): AmaronJourneyDisposeSubmitArgs {
    return {
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.workspaceId,
        journeyId: args.event.eventContainer.id,
        dispositionId: args.dispositionId,
        remarks: args.remarks,
        remarksColumnId: args.remarksColumnId,
        triggerEventAdditionalColumnValues: args.triggerEventAdditionalColumnValues,
        contextEventAdditionalColumnValues: args.event.additionalColumnValues,
        journeyAdditionalColumnValues: args.event.eventContainer.additionalColumnValues,
    };
}

/** Journey profile details tab: resolve first enquiry event for dispose context when available. */
export function buildAmaronJourneyDisposeArgsFromJourneyProfile(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    journey: EventContainerWithEvents;
    dispositionId: Uuid;
    remarks: string | null;
    remarksColumnId?: Uuid;
    triggerEventAdditionalColumnValues?: Record<string, unknown>;
    journeyAdditionalColumnValues: Record<string, unknown>;
}): AmaronJourneyDisposeSubmitArgs {
    const firstEnquiryEvent = findFirstAmaronEnquiryEventOnJourney(
        args.journey,
    );

    return {
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.workspaceId,
        journeyId: args.journey.id,
        dispositionId: args.dispositionId,
        remarks: args.remarks,
        remarksColumnId: args.remarksColumnId,
        triggerEventAdditionalColumnValues: args.triggerEventAdditionalColumnValues,
        journeyAdditionalColumnValues: args.journeyAdditionalColumnValues,
        contextEventAdditionalColumnValues: firstEnquiryEvent?.additionalColumnValues,
        firstEnquiryEventId: firstEnquiryEvent?.id,
        firstEnquiryStatusRaw:
            firstEnquiryEvent?.additionalColumnValues[AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID],
    };
}
